import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Hidden,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  AddCircleOutlineOutlined,
  Search as SearchIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useDebounce } from "use-debounce";
import { theme } from "../../themes/theme";
import { getContentById, showMessage, toggleNavCollapsed } from "../../redux/actions";
import { fontColor } from "../../constants/ColorSets";
import { RuleOfTheWeekDialog } from "./RuleOfTheWeekDialog";
import { AppState } from "../../redux/store";
import { RuleUnitState } from "../../types/models/RuleUnitState";
import {
  getModuleNameFromTaxonomy,
  taxonomyMatchesModule,
  getTaxonomyContentSection,
} from "../../utils/RulesOfTheWeekUtils";
import { RuleInstance } from "../../types/models/RuleInstance";
import { isCurrentAccountAdmin } from "../../components/AppBase/AppAuthProvider";
import { deleteAdminRuleOfWeek, getAdminRulesOfWeeks } from "../../redux/actions/RuleOfWeekAdmin";
import { RuleOfWeekResponse } from "../../types/models/RuleOfWeekResponse";
import AppConfirmDialog from "../../components/AppBase/AppConfirmDialog";
import AppInfoView from "../../components/AppBase/AppInfoView";
import RulesOfTheWeekTable from "./RulesOfTheWeekTable";
import { RoleState } from "../../types/models/RoleState";

export default function RulesOfTheWeekPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isScreenLessThanSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const ruleUnitState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const roleState = useSelector<AppState, RoleState>((state) => state.role);

  const [currentRuleId, setCurrentRuleId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [ruleDialogOpen, setRuleDialogOpen] = useState<boolean>(false);
  const [currentMode, setCurrentMode] = useState<string>("create");
  const [editRecord, setEditRecord] = useState<RuleOfWeekResponse | undefined>(undefined);
  const [ruleModules, setRuleModules] = useState<string[]>([]);
  const [ruleContents, setRuleContents] = useState<
    { header: string; contents: { name: string; id: string }[] }[]
  >([]);
  const [rules, setRules] = useState<RuleInstance[]>([]);
  const [ruleOfTheWeeks, setRuleOfTheWeeks] = useState<RuleOfWeekResponse[]>();
  const [filteredRuleOfTheWeeks, setFilteredRuleOfTheWeeks] = useState<RuleOfWeekResponse[]>();
  const [showExpired, setShowExpired] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText] = useDebounce(searchText, 700);

  const isAuthor = roleState.contentAuthor ? roleState.contentAuthor : false;

  useEffect(() => {
    const isAdmin = isCurrentAccountAdmin();
    if (!isAdmin && !isAuthor) {
      navigate(-1);
      return;
    }

    const modules: string[] = [];
    ruleUnitState.baseContentRevisions?.forEach((contents, module) => {
      const moduleName = getModuleNameFromTaxonomy(module);
      if (!modules.includes(moduleName)) {
        modules.push(moduleName);
      }
    });
    setRuleModules(modules);

    getRuleOfTheWeeks();
  }, []);

  const getRuleOfTheWeeks = () => {
    setLoading(true);
    dispatch(
      getAdminRulesOfWeeks((rulesOfWeeks) => {
        setRuleOfTheWeeks(rulesOfWeeks);
        setLoading(false);
      })
    );
  };

  useEffect(() => {
    let filtered = ruleOfTheWeeks;
    if (!showExpired) {
      filtered = filtered?.filter((row) => !row.ROTWEnd || new Date(row.ROTWEnd) >= new Date());
    }
    if (debouncedSearchText && debouncedSearchText.trim() !== "") {
      filtered = filtered?.filter(
        (row) =>
          row.DisplayName.toLowerCase().includes(debouncedSearchText.toLowerCase()) ||
          row.TaxonomyTitle?.toLowerCase().includes(debouncedSearchText.toLowerCase()) ||
          row.RuleDisplayName.toLowerCase().includes(debouncedSearchText.toLowerCase())
      );
    }
    setFilteredRuleOfTheWeeks(filtered);
  }, [ruleOfTheWeeks, debouncedSearchText, showExpired]);

  const getContentsFromModule = (moduleName: string) => {
    const tmpCont: { header: string; contents: { name: string; id: string }[] }[] = [];
    ruleUnitState.baseContentRevisions?.forEach((cont) => {
      const fil = cont.filter((c) => taxonomyMatchesModule(c.Taxonomy, moduleName));
      if (fil && fil.length > 0) {
        const header = getTaxonomyContentSection(fil[0].Taxonomy);
        const contentNames = fil.map((f) => ({ name: f.DisplayName, id: f.ContentRevisionId }));
        tmpCont.push({ header, contents: contentNames });
      }
    });
    setRuleContents(tmpCont);
  };

  const getRulesFromContent = (contentId: string) => {
    dispatch(
      getContentById(contentId, (rule) => {
        if (rule?.RuleInstances) {
          setRules(rule?.RuleInstances);
        }
      })
    );
  };

  const handleRuleDeletion = (id: string) => {
    setCurrentRuleId(id);
    setDeleteDialogOpen(true);
  };

  const handleCancelDeletion = () => {
    setDeleteDialogOpen(false);
  };

  const handleEditRule = (row: RuleOfWeekResponse) => {
    setEditRecord(row);
    setCurrentMode("edit");
    setRuleDialogOpen(true);
  };

  const handleCreateRule = () => {
    setEditRecord(undefined);
    setCurrentMode("create");
    setRuleDialogOpen(true);
  };

  const handleDeleteRule = (id: string) => {
    dispatch(
      deleteAdminRuleOfWeek(id, (result) => {
        if (result) {
          dispatch(showMessage("The rule of the week was deleted successfully."));
          getRuleOfTheWeeks();
        }
        setCurrentRuleId("");
        setDeleteDialogOpen(false);
      })
    );
  };

  return (
    <Box sx={{ backgroundColor: theme.page.bgColor, minHeight: "100vh" }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <Hidden lgUp>
            <IconButton
              sx={{
                color: "text.secondary",
                marginLeft: 1,
                padding: 0,
              }}
              edge="start"
              className="menu-btn"
              color="inherit"
              aria-label="open drawer"
              onClick={() => dispatch(toggleNavCollapsed())}
              size="large"
            >
              <MenuIcon
                sx={{
                  width: 35,
                  height: 35,
                }}
              />
            </IconButton>
          </Hidden>
          <Box sx={{ py: 1 }}>
            <Typography
              variant="h3"
              component="h3"
              color={fontColor.titleFontColor}
              style={{ fontSize: 22, marginLeft: 12 }}
            >
              Rules of the Week
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            mr: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <TextField
            id="search"
            type="search"
            label="Search"
            variant="standard"
            value={searchText}
            sx={{ width: isScreenLessThanSmall ? 120 : 200 }}
            onChange={(event) => setSearchText(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{ mr: 0, mt: 2, display: "flex", justifyContent: "flex-end", alignItems: "center" }}
          >
            <FormControlLabel
              control={
                <Switch checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)} />
              }
              label={
                <Typography variant="body2" noWrap>
                  Show Expired
                </Typography>
              }
            />
            <Button
              startIcon={<AddCircleOutlineOutlined />}
              disabled={loading}
              variant="text"
              sx={{ textTransform: "none", borderRadius: 1, whiteSpace: "nowrap" }}
              onClick={handleCreateRule}
            >
              Add Rule
            </Button>
          </Box>
        </Box>
      </Box>
      <Box sx={{ borderBottom: "1px solid lightgrey", mx: 1.5 }}></Box>
      <Box sx={{ m: 4 }}>
        <RulesOfTheWeekTable
          loading={loading}
          ruleOfTheWeeks={ruleOfTheWeeks}
          filteredRuleOfTheWeeks={filteredRuleOfTheWeeks}
          onEdit={handleEditRule}
          onDelete={handleRuleDeletion}
        />
      </Box>
      <AppConfirmDialog
        dialogTitle="Delete Rule"
        onConfirm={() => handleDeleteRule(currentRuleId)}
        open={deleteDialogOpen}
        title="Are you sure you want to delete this rule?"
        onDeny={handleCancelDeletion}
      />
      <RuleOfTheWeekDialog
        isOpen={ruleDialogOpen}
        mode={currentMode}
        setIsOpen={setRuleDialogOpen}
        ruleModules={ruleModules}
        getContentsFromModule={getContentsFromModule}
        ruleContents={ruleContents}
        getRulesFromContent={getRulesFromContent}
        rules={rules}
        ruleUnitState={ruleUnitState}
        editRecord={editRecord}
        onSaved={getRuleOfTheWeeks}
      />
      <AppInfoView />
    </Box>
  );
}
